import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import { excludeCorporateRedirection, Validate, getAssetPath } from "@commonUtils"
import { directAxiosInstance } from "@lib/api"
import InputField from "@components/nonDLSComponents/InputField/InputField.js"
import Button from "@components/Button/Button"
import {
    verifyConflictingEmailGA,
    verifyPhoneOtpConflictGA,
    verifyPhoneOtpGA,
    resendOtpOnCallGA,
    resendOtpOnSmsGA,
    resendOtpOnEmailGA,
    changeNumberGA,
} from "../GAEvents"
import css from "../Auth.scss"
import { setDefault } from "../reducer"
import { useDispatch, useSelector } from "react-redux"
import { setDefaultAuthConfig } from "@containers/App/reducer"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"
const OtpConflict = ({ backHandler, onSuccess, forwardHandler, state, localDispatch }) => {
    const { authConfig } = useSelector((state) => state.shellReducer)
    const dispatch = useDispatch()
    const { info, conflictInfo } = state
    const [timeLeft, setTimeLeft] = useState(30)
    const [error, setError] = useState(null)

    // we are using "active" as a flag so that untill the request is completed, we do not allow multiple request
    // on multiple clicks on cta
    const [active, setActive] = useState(false)
    const [ctaLabel, setCtaLabel] = useState("Continue")
    const [otp, setOtp] = useState("")
    const [alertType, setAlertType] = useState("error")
    const [iconUrl, setIconUrl] = useState(null)
    useEffect(() => {
        if (timeLeft > 0) {
            setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
        }
    }, [timeLeft])
    const [focused, setFocused] = useState(false)
    const onFocus = () => {
        setFocused(true)
    }

    const onBlur = () => {
        if (otp === "") {
            setFocused(false)
        }
    }
    /**
     * @description when user come on this screen cursor should be on inputField .
     */
    const inputRef = useRef(null)
    useEffect(() => {
        setFocused(true)
        inputRef.current.focus()
    }, [])
    /**
     *
     * @description Function to submit the form by clicking on enter
     */
    const onKeyUp = (event) => {
        if (event.key === "Enter") {
            confirmOtp()
        }
    }
    /**
     * @description Function to check valid input
     */

    const errorCheck = () => {
        setError(null)
        const errs = Validate({
            otp: otp,
        })
        setError(errs)
        if (errs) {
            setAlertType("error")
        }
        return errs
    }

    /**
     * @description The following function handles multiple flows :-
     *              1. If we have email conflict, then we request for "auth/phone/verify_token_conflict_resolve"
     *              2. If we have phone conflict, then we request for "auth/email/verify_token_conflict"
     *              3. If the user tries to logs in through email and phone number is not attached to the same, then
     *                 we request for "auth/email/verify_phone_token"
     */

    const confirmOtp = () => {
        const errs = errorCheck()

        if (!errs && !active) {
            setActive(true)
            setIconUrl(getAssetPath("svgs/icons/loading-wheel_24px_background.svg"))

            directAxiosInstance()
                .post(
                    conflictInfo.conflict
                        ? conflictInfo.conflictEmail
                            ? "auth/phone/verify_token_conflict_resolve"
                            : "auth/email/verify_token_conflict"
                        : "auth/email/verify_phone_token",
                    {
                        number: conflictInfo.number ? conflictInfo.number : info.phone,
                        email: conflictInfo.conflictEmail ? conflictInfo.conflictEmail : info.email,
                        verification_token: otp,
                    }
                )
                .then((res) => {
                    if (res && res.data) {
                        const userData = res.data?.user_data || null
                        if (userData) {
                            if (conflictInfo.conflict) {
                                conflictInfo.conflictEmail
                                    ? verifyConflictingEmailGA("Success")
                                    : verifyPhoneOtpConflictGA("Success")
                            } else {
                                verifyPhoneOtpGA("Success")
                            }
                            const corporateRedirectionUrl = excludeCorporateRedirection()
                            onSuccess(userData, corporateRedirectionUrl)
                        }
                    }
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    setActive(false)
                })
                .catch((err) => {
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    setTimeLeft(0)
                    setActive(false)
                    const errMsg = err?.data?.error?.message || "Server error"
                    setError({ otp: errMsg })
                    setAlertType("error")
                    if (conflictInfo.conflict) {
                        conflictInfo.conflictEmail
                            ? verifyConflictingEmailGA("Error")
                            : verifyPhoneOtpConflictGA("Error")
                    } else {
                        verifyPhoneOtpGA("Error")
                    }
                })
        }
    }

    /**
     * @description Function to resend otp for conflict email
     */

    const resendOtpEmail = () => {
        const options = {
            url: "auth/phone/create_token_conflict_resolve",
            method: "POST",
            data: {
                number: info.phone,
                email: conflictInfo.conflictEmail,
            },
        }
        directAxiosInstance()(options)
            .then((res) => {
                if (res && res.data) {
                    if (!res.data.errors && !res.data.error) {
                        setTimeLeft(30)
                        setError({ otp: `You will receive an email with OTP` })
                        setTimeout(() => {
                            setError({
                                otp: null,
                            })
                        }, 30000)
                        setAlertType("success")
                        resendOtpOnEmailGA("Verify Existing Email id-Conflict", "Success")
                    } else {
                        const err = res.data.error.message
                        setError({ otp: err })
                        setAlertType("error")
                        resendOtpOnEmailGA("Verify Existing Email id-Conflict", "Error")
                    }
                }
            })
            .catch((err) => {
                const errMsg = err?.data?.error?.message || "Server error"
                setError({ otp: errMsg })
                setAlertType("error")
                resendOtpOnEmailGA("Verify Existing Email id-Conflict", "Error")
            })
    }

    /**
     * @description Function to resend otp for conflict phone or to attach phone number for the same
     */

    const resendOtpPhone = (onCall = false) => {
        const options = {
            url: conflictInfo.conflict ? "auth/email/create_token_conflict" : "auth/email/complete_login",
            method: "POST",
            data: {
                number: conflictInfo.number || info.phone,
                email: conflictInfo.conflictEmail || info.email,
                otp_on_call: onCall,
            },
        }
        directAxiosInstance()(options)
            .then((res) => {
                if (res && res.data) {
                    if (!res.data?.errors && !res.data?.error) {
                        setTimeLeft(30)
                        setError({
                            otp: `You will receive ${onCall ? "a" : "an"} ${
                                onCall ? "call" : "sms"
                            } with OTP`,
                        })
                        setTimeout(() => {
                            setError({
                                otp: null,
                            })
                        }, 30000)
                        setAlertType("success")
                        if (onCall) {
                            conflictInfo.conflict
                                ? resendOtpOnCallGA("Verify mobile number - Conflict", "Success")
                                : resendOtpOnCallGA("Verify mobile number - login", "Success")
                        } else {
                            conflictInfo.conflict
                                ? resendOtpOnSmsGA("Verify mobile number - Conflict", "Success")
                                : resendOtpOnSmsGA("Verify mobile number - login", "Success")
                        }
                    } else {
                        const err = res?.data?.error?.message
                        setError({ otp: err })
                        setAlertType("error")
                        if (onCall) {
                            conflictInfo.conflict
                                ? resendOtpOnCallGA("Verify mobile number - Conflict", "Success")
                                : resendOtpOnCallGA("Verify mobile number - login", "Success")
                        } else {
                            conflictInfo.conflict
                                ? resendOtpOnSmsGA("Verify mobile number - Conflict", "Success")
                                : resendOtpOnSmsGA("Verify mobile number - login", "Success")
                        }
                    }
                }
            })
            .catch((err) => {
                const errMsg = err?.data?.error?.message || "Server error"
                setError({ otp: errMsg })
                setAlertType("error")
                if (onCall) {
                    conflictInfo.conflict
                        ? resendOtpOnCallGA("Verify mobile number - Conflict", "Success")
                        : resendOtpOnCallGA("Verify mobile number - login", "Success")
                } else {
                    conflictInfo.conflict
                        ? resendOtpOnSmsGA("Verify mobile number - Conflict", "Success")
                        : resendOtpOnSmsGA("Verify mobile number - login", "Success")
                }
            })
    }

    return (
        <>
            <div className={css.rightContainer}>
                {!authConfig.restrictUserFromClosingAuth && (
                    <div className={` flex ${css.crossIcon} ${css.cursorPointer}`}>
                        <div>
                            <ARIAButton
                                onClick={() => {
                                    dispatch(setDefaultAuthConfig())
                                    localDispatch(setDefault())
                                }}
                                ariaLabel={"Close otp conflict popup"}
                            >
                                <img
                                    src={getAssetPath("svgs/icons/cross_18px_primary.svg")}
                                    alt={"Close icon"}
                                />
                            </ARIAButton>
                        </div>
                    </div>
                )}
                <div className={css.authContainer}>
                    <div className={`${css.otpContainer} flex flexColumn justifyBetween `}>
                        <div>
                            <span className={css.heading}>{`Verify
                                ${
                                    !conflictInfo.conflictEmail
                                        ? conflictInfo.number ||
                                          (!conflictInfo.number && !conflictInfo.conflictEmail)
                                            ? "mobile number"
                                            : "email Id"
                                        : "email id"
                                }

                                    `}</span>
                            <div className={`smallRegular ${css.subHeadingMargin}`}>
                                Provide OTP sent to{" "}
                                <span>
                                    {!conflictInfo.conflictEmail
                                        ? conflictInfo.number
                                            ? conflictInfo.number
                                            : info.phone
                                        : conflictInfo.conflictEmail}
                                </span>
                                <ConditionalLink
                                    className={`textAction smallSemiBold ${css.marginLeft4}`}
                                    onClick={() => {
                                        changeNumberGA("Login")
                                        backHandler()
                                    }}
                                >
                                    <u>Edit</u>
                                </ConditionalLink>
                            </div>
                            <div>
                                <span className={css.marginBottom16} data-testid="inputField_otpconflict">
                                    <InputField
                                        label={"One Time Password"}
                                        onChange={setOtp}
                                        alertMessage={error && error.otp}
                                        countDown={timeLeft ? `Resend in 00:${timeLeft}` : ""}
                                        focused={focused}
                                        onFocus={() => onFocus()}
                                        onBlur={() => onBlur()}
                                        borderType={"primary"}
                                        onKeyUp={(e) => onKeyUp(e)}
                                        alertType={alertType}
                                        ref={inputRef}
                                    />
                                </span>
                                <div className="flex">
                                    {(conflictInfo.number && !conflictInfo.conflictEmail) ||
                                        (info.phone && (
                                            <div className="marginTop-16 flex justifyAround">
                                                {!timeLeft && (
                                                    <>
                                                        <ConditionalLink
                                                            className={`textAction bodySemiBold ${css.cursorPointer}`}
                                                            onClick={() => resendOtpPhone()}
                                                        >
                                                            Get OTP on SMS
                                                        </ConditionalLink>
                                                        <div className={`${css.marginBoth12} textSecondary`}>
                                                            |
                                                        </div>
                                                        <ConditionalLink
                                                            className={`textAction bodySemiBold ${css.cursorPointer}`}
                                                            onClick={() => resendOtpPhone(true)}
                                                        >
                                                            Get OTP on Call
                                                        </ConditionalLink>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    {((conflictInfo.number && !conflictInfo.conflictEmail) || info.phone) &&
                                        conflictInfo.conflictEmail && (
                                            <>
                                                {!timeLeft && (
                                                    <>
                                                        <div
                                                            className={`marginTop-16 ${css.marginBoth12} textSecondary`}
                                                        >
                                                            |
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    {conflictInfo.conflictEmail && (
                                        <div className={` marginTop-16 flex alignCenter`}>
                                            {!timeLeft && (
                                                <ConditionalLink
                                                    className={`textAction bodySemiBold ${css.cursorPointer}`}
                                                    onClick={() => resendOtpEmail()}
                                                >
                                                    Get OTP on Email
                                                </ConditionalLink>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className=" flex flexColumn justifyBetween  ">
                            <Button
                                label={!iconUrl ? ctaLabel : null}
                                iconUrl={iconUrl}
                                isDisabled={iconUrl ? true : false}
                                onClick={confirmOtp}
                            />
                            <div className="marginTop-64 flexColumn alignCenter">
                                <div className={css.footerSubText}>
                                    <ConditionalLink
                                        className={`textAction xSmallSemiBold ${css.cursorPointer}`}
                                        onClick={() => forwardHandler("NeedHelp")}
                                    >
                                        <span>Need Help?</span>
                                        <span className={css.marginLeft4}> Get in touch</span>
                                    </ConditionalLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

OtpConflict.propTypes = {
    backHandler: PropTypes.func,
    forwardHandler: PropTypes.func,
    onSuccess: PropTypes.func,
    state: PropTypes.shape({
        info: PropTypes.shape({
            email: PropTypes.string,
            phone: PropTypes.string,
        }),
        conflictInfo: PropTypes.shape({
            conflict: PropTypes.bool,
            number: PropTypes.string,
            conflictEmail: PropTypes.string,
        }),
    }).isRequired,
    localDispatch: PropTypes.func.isRequired,
}

export default OtpConflict
